/**
 * useChatAutoStart
 * Responsabilidade: Gerenciar auto-início de conversas vindas do Kanban
 * Princípio: Single Responsibility
 */

import { useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface AutoStartData {
    number: string;
    name: string;
    timestamp: number;
}

/** Deep link de push: /conversas?c=<conversationDbId> */
export function useChatPushDeepLink(
    organizationId: string | undefined,
    selectConversation: (id: string | null, connectionId?: string | null) => void,
) {
    const handledRef = useRef(false);

    useEffect(() => {
        if (handledRef.current) return;
        if (!organizationId) return;

        const conversationDbIdFromUrl = new URLSearchParams(window.location.search).get('c');
        if (!conversationDbIdFromUrl) return;

        handledRef.current = true;

        const url = new URL(window.location.href);
        url.searchParams.delete('c');
        window.history.replaceState(null, '', url.pathname + url.search);

        void (async () => {
            const { data } = await supabase
                .from('chat_conversations')
                .select('remote_jid, connection_id')
                .eq('id', conversationDbIdFromUrl)
                .eq('organization_id', organizationId)
                .maybeSingle();

            selectConversation(
                data?.remote_jid ?? conversationDbIdFromUrl,
                data?.connection_id ?? null,
            );
        })();
    }, [organizationId, selectConversation]);
}

export const useChatAutoStart = (
    startNewConversation: (number: string, name: string) => void,
    isReady: boolean
) => {
    useEffect(() => {
        if (!isReady) return;

        // Caso 1: Vindo do Kanban / Contatos (objeto com timestamp)
        try {
            const autoStartData = localStorage.getItem('chat_auto_start');
            if (autoStartData) {
                const { number, name, timestamp }: AutoStartData = JSON.parse(autoStartData);
                const isRecent = Date.now() - timestamp < 30000;

                if (isRecent && number) {
                    localStorage.removeItem('chat_auto_start');
                    startNewConversation(number, name);
                    return;
                }

                localStorage.removeItem('chat_auto_start');
            }
        } catch (e) {
            console.error(e);
        }

        // Caso 2: Vindo de cartão de contato/mídia (apenas número)
        const directNumber = localStorage.getItem('CHAT_OPEN_CONVERSATION_PHONE');
        if (directNumber) {
            localStorage.removeItem('CHAT_OPEN_CONVERSATION_PHONE');
            startNewConversation(directNumber, directNumber);
        }
    }, [startNewConversation, isReady]);
};
